import { useEffect, useRef, useState } from 'react'
import { Box, Modal, TextField, Button, Checkbox, FormControlLabel } from '@mui/material'
import { brand, fontFamily, headingSx, subtextSx, btnPrimarySx, btnSecondarySx } from './protoTheme'

const ZONE_PRESETS = [360, 270, 180, 90]

const emptyData = { deviceId: '', deviceName: 'Pivot 1', zoneWidth: null, zoneWidthCustom: '' }

// existingDevice: resuming calibration on an already-registered device - its
// deviceId is permanent, so step 1 shows it read-only instead of collecting
// a new one. Current angle/offset isn't collected here at all: it can only
// ever be set once the device itself reports calibrated (calibState 'done').
const AddDeviceWizard = ({ open, onClose, onFinish, startStep = 1, initialData = {}, title, existingDevice = false, deviceOnline = true, submitting = false }) => {
    const [step, setStep] = useState(startStep)
    const [data, setData] = useState({ ...emptyData, ...initialData })

    // Real camera scan (QR or 1D barcode - device stickers might be either).
    // The zxing library is loaded on demand so people who never scan don't
    // pay for it on a slow connection.
    const [scanning, setScanning] = useState(false)
    const [scanError, setScanError] = useState(null)
    const videoRef = useRef(null)
    const codeReaderRef = useRef(null)

    useEffect(() => {
        if (open) {
            setStep(startStep)
            setData({ ...emptyData, ...initialData })
            setScanning(false)
            setScanError(null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, startStep])

    useEffect(() => {
        if (!scanning) return
        let cancelled = false

        import('@zxing/browser').then(({ BrowserMultiFormatReader }) => {
            if (cancelled) return
            const codeReader = new BrowserMultiFormatReader()
            codeReader
                .decodeFromConstraints(
                    // `ideal`, not `exact` - older/cheaper phones with a
                    // non-standard camera list throw OverconstrainedError on
                    // exact and the scan never starts. Small ideal resolution
                    // keeps per-frame decode cheap on weak CPUs - a printed
                    // sticker doesn't need more than this to read.
                    { video: { facingMode: { ideal: 'environment' }, width: { ideal: 640 }, height: { ideal: 480 } } },
                    videoRef.current,
                    (result, err) => {
                        if (result) {
                            setData((d) => ({ ...d, deviceId: result.getText() }))
                            setScanning(false)
                        }
                        // err fires continuously between frames with no code
                        // in view (NotFoundException) - not a real error.
                    }
                )
                .then((controls) => {
                    if (cancelled) { controls.stop(); return }
                    codeReaderRef.current = controls
                })
                .catch((e) => {
                    if (cancelled) return
                    const message = e?.name === 'NotAllowedError'
                        ? "Camera access denied - enter the ID manually below"
                        : e?.name === 'NotFoundError'
                            ? "No camera found - enter the ID manually below"
                            : "Couldn't start the camera - enter the ID manually below"
                    setScanError(message)
                    setScanning(false)
                })
        })

        return () => {
            cancelled = true
            codeReaderRef.current?.stop()
            codeReaderRef.current = null
        }
    }, [scanning])

    const zoneWidth = data.zoneWidth === 'custom' ? Number(data.zoneWidthCustom) : data.zoneWidth

    const handleScan = () => {
        if (!navigator.mediaDevices?.getUserMedia) {
            setScanError("Camera scanning isn't supported on this device - enter the ID manually below")
            return
        }
        setScanError(null)
        setScanning(true)
    }

    const canGoStep2 = data.deviceId.trim().length > 0 && data.deviceName.trim().length > 0
    const canGoStep3 = data.zoneWidth != null && (data.zoneWidth !== 'custom' || (Number(data.zoneWidthCustom) > 0 && Number(data.zoneWidthCustom) <= 360))

    const [ackChecked, setAckChecked] = useState(false)
    useEffect(() => { if (open) setAckChecked(false) }, [open, startStep])

    const finish = (startNow) => {
        onFinish({
            deviceId: data.deviceId,
            deviceName: data.deviceName,
            zoneWidth,
            startNow
        })
    }

    const zoneCopy = zoneWidth === 360
        ? 'The machine must complete a full rotation in one continuous pass, without powering off.'
        : `The machine must travel all ${zoneWidth || '…'}° to the stop and turn around in one continuous pass, without powering off.`

    const presetSx = (selected) => selected
        ? { ...btnPrimarySx, minWidth: 64 }
        : { ...btnSecondarySx, minWidth: 64 }

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: 460, maxWidth: '92vw', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2,
                maxHeight: '90vh', overflowY: 'auto', fontFamily
            }}>
                <div style={{ ...subtextSx, fontSize: 12, marginBottom: 4 }}>{title || 'Add device'} · step {step} of 3</div>

                {step === 1 && (
                    <Box>
                        <h3 style={headingSx}>Add device</h3>
                        {!existingDevice && (
                            <>
                                {scanning ? (
                                    <Box sx={{ mt: 2, mb: 1 }}>
                                        <video
                                            ref={videoRef}
                                            style={{ width: '100%', borderRadius: 8, background: '#000' }}
                                            muted playsInline
                                        />
                                        <Button variant="outlined" fullWidth sx={{ ...btnSecondarySx, mt: 1 }} onClick={() => setScanning(false)}>Cancel</Button>
                                    </Box>
                                ) : (
                                    <Button variant="outlined" fullWidth sx={{ ...btnSecondarySx, mt: 2, mb: 1 }} onClick={handleScan}>📷 Scan code</Button>
                                )}
                                {scanError && <div style={{ fontFamily, fontSize: 12, color: '#B60000', marginBottom: 8 }}>{scanError}</div>}
                                <div style={{ ...subtextSx, fontSize: 12, margin: '8px 0' }}>Or enter manually</div>
                            </>
                        )}
                        <TextField
                            label="Device ID" fullWidth variant="standard" sx={{ mb: 2, mt: existingDevice ? 2 : 0 }}
                            value={data.deviceId} onChange={(e) => setData({ ...data, deviceId: e.target.value })}
                            helperText={existingDevice ? "Permanent - can't be changed after the device is added" : "24-character ID from the sticker on the case"}
                            disabled={existingDevice}
                        />
                        <TextField
                            label="Device name" fullWidth variant="standard" sx={{ mb: 2 }}
                            value={data.deviceName} onChange={(e) => setData({ ...data, deviceName: e.target.value })}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button variant="contained" sx={btnPrimarySx} disabled={!canGoStep2} onClick={() => setStep(2)}>Next</Button>
                        </Box>
                    </Box>
                )}

                {step === 2 && (
                    <Box>
                        <h3 style={headingSx}>How many degrees does your pivot sweep?</h3>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1, mt: 2 }}>
                            {ZONE_PRESETS.map((p) => (
                                <Button
                                    key={p} variant={data.zoneWidth === p ? 'contained' : 'outlined'}
                                    sx={presetSx(data.zoneWidth === p)}
                                    onClick={() => setData({ ...data, zoneWidth: p })}
                                >{p}°</Button>
                            ))}
                            <Button variant={data.zoneWidth === 'custom' ? 'contained' : 'outlined'} sx={presetSx(data.zoneWidth === 'custom')} onClick={() => setData({ ...data, zoneWidth: 'custom' })}>Other</Button>
                        </Box>
                        {data.zoneWidth === 'custom' && (
                            <TextField
                                label="Zone width (°)" type="number" variant="standard" sx={{ mb: 2 }}
                                value={data.zoneWidthCustom} onChange={(e) => setData({ ...data, zoneWidthCustom: e.target.value })}
                            />
                        )}
                        <div style={{ ...subtextSx, fontSize: 12, margin: '12px 0' }}>
                            Not sure? Check the panel when the machine is parked at each stop, and subtract one value from the other.
                        </div>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Button sx={{ ...btnSecondarySx, border: 'none' }} onClick={() => setStep(1)}>Back</Button>
                            <Button variant="contained" sx={btnPrimarySx} disabled={!canGoStep3} onClick={() => setStep(3)}>Next</Button>
                        </Box>
                    </Box>
                )}

                {step === 3 && (
                    <Box>
                        <h3 style={headingSx}>The first pass is the calibration run</h3>
                        <div style={{ fontFamily, fontSize: 13, marginTop: 12, marginBottom: 12 }}>
                            {zoneCopy} Duration depends on your pivot's settings.
                        </div>
                        <Box sx={{ fontFamily, border: `1px solid ${brand.navy}22`, bgcolor: 'rgba(1,0,53,0.03)', borderRadius: 1, p: 1.5, mb: 2, fontSize: 13 }}>
                            Tip: start when the machine is sure to run without stopping, for example at the start of a watering cycle.
                            "Start later" does this for you - calibration starts automatically the first time the device connects, so make sure the pivot will be running then.
                        </Box>
                        {!deviceOnline && (
                            <Box sx={{ fontFamily, border: '1px solid #B6030144', bgcolor: 'rgba(182,3,1,0.05)', borderRadius: 1, p: 1.5, mb: 2, fontSize: 13, color: '#B60301' }}>
                                Device is offline - it can't receive the start command right now. Use "Start later".
                            </Box>
                        )}
                        <FormControlLabel
                            sx={{ '& .MuiFormControlLabel-label': { fontFamily, fontSize: 14 } }}
                            control={<Checkbox checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} sx={{ color: brand.navy, '&.Mui-checked': { color: brand.navy } }} />}
                            label="Understood, the machine will complete a full cycle"
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Button sx={{ ...btnSecondarySx, border: 'none' }} onClick={() => setStep(2)}>Back</Button>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button variant="outlined" sx={btnSecondarySx} disabled={submitting} onClick={() => finish(false)}>Start later</Button>
                                <Button variant="contained" sx={btnPrimarySx} disabled={!ackChecked || !deviceOnline || submitting} onClick={() => finish(true)}>Start calibration</Button>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Modal>
    )
}

export default AddDeviceWizard
